import { getTopLevelDomain } from '@snapchat/parse-domain';

import { logInfo } from '../../../helpers/logging';
import type { CookieManager } from '../../../utils/cookies/CookieManager';

const scPartnerCookie = 'sc-partner';
const scPartnerQueryParams = 'sc_partner';

interface ScPartnerValue {
  id: string;
  refId: string;
  ref: string;
}

export const handlePartnerstackQueryParam = (
  cookieManager: CookieManager,
  acceptsMarketing: boolean
): void => {
  // we always want to consolidate the query params regardless of
  // user acceptance of marketing cookies
  const urlParams = new URLSearchParams(window.location.search);
  const psPartnerKey = urlParams.get('ps_partner_key');
  const psXid = urlParams.get('ps_xid');

  // if we are missing either one, we abort
  if (!psPartnerKey || !psXid) {
    return;
  }

  const partnerData: ScPartnerValue = {
    id: psXid,
    refId: psPartnerKey,
    // this value maps to a value in ads api, so we need it to be consistent
    // https://github.sc-corp.net/Snapchat/snapchat3/blob/main/libs/admanager-api-wire-model/src/main/java/
    // snapchat/admanager/api/wiremodel/organization/OrganizationReferrer.java
    ref: 'PARTNERSTACK',
  };

  // We don't url encode component because that automatically happens when we replace state
  const partnerDataString = btoa(JSON.stringify(partnerData));

  // remove the query params from the URL since we are replacing it with our own query param
  urlParams.delete('ps_partner_key');
  urlParams.delete('ps_xid');
  // partnerstack adds these query params too, but we don't need them
  urlParams.delete('gspk');
  urlParams.delete('gsxid');
  urlParams.set(scPartnerQueryParams, partnerDataString);

  const newUrl = new URL(window.location.href);
  newUrl.search = urlParams.toString();
  window.history.replaceState(null, '', newUrl.toString());

  let cookieDomain = getTopLevelDomain(window.location.hostname);
  // need leading .
  if (cookieDomain !== 'localhost') cookieDomain = `.${cookieDomain}`;

  // only set cookies if user has accepted marketing cookies
  if (acceptsMarketing) {
    const cookieOptions = {
      // 90 days
      expires: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      secure: true,
      httpOnly: false,
      credentials: 'same-origin',
      domain: getTopLevelDomain(cookieDomain),
    };

    cookieManager.setCookie(scPartnerCookie, partnerDataString, cookieOptions);
  }

  logInfo({
    eventAction: 'Partnerstack Query Consolidated',
    eventCategory: 'partnerstack',
    eventLabel: `Consolidated Partnerstack Query Params. Marketing Accepted: ${acceptsMarketing}`,
  });
};
